import type { AudioProps } from "../../../@types/viewerati";
import { resolveColorChoice } from "../../../lib/cssHelpers";
import { mainCssRules } from "../../../lib/cssTemplates";
import modifierStyled from "../../../lib/modifierStyled";
import styled from "styled-components";

/* Tufte-quiet audio card: round play button beside a titled progress bar,
   every color from the presentation palette (ink = first, container tint =
   seventh with a light ink mix as fallback). */
const StyledAudioPlayer = styled.div<AudioProps>`
  width: 100%;
  /* measure + centring come from the slide container's .audio rule —
     declared there because elementAlign emits justify-self for every
     element and would override anything written here */

  .audio-player {
    display: flex;
    align-items: center;
    gap: 1em;
    padding: 0.9em 1.3em;
    border-radius: 10px;
    background: ${(props) => {
      const tint = resolveColorChoice("seventh", props.$filterColors);
      return tint !== "unset"
        ? tint
        : `color-mix(in srgb, ${props.$filterColors.first ?? "#111"} 7%, transparent)`;
    }};
  }

  .audio-controls button {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;
    flex-shrink: 0;

    &:focus-visible {
      outline: 3px solid #4a90d9;
      outline-offset: 2px;
    }
  }

  .audio-controls svg {
    width: 1.5em;
    height: 1.5em;

    path {
      fill: ${(props) => props.$filterColors.first ?? "#111"};
    }
  }

  .track-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
  }

  .title {
    font-size: 0.8em;
    color: ${(props) => props.$filterColors.first ?? "#111"};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    margin: 0;
    border-radius: 999px;
    cursor: pointer;
    accent-color: ${(props) => props.$filterColors.first ?? "#111"};

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px;
      height: 14px;
      margin-top: -4px;
      border: none;
      border-radius: 50%;
      background: ${(props) => props.$filterColors.first ?? "#111"};
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border: none;
      border-radius: 50%;
      background: ${(props) => props.$filterColors.first ?? "#111"};
    }

    &:focus-visible {
      outline: 3px solid #4a90d9;
      outline-offset: 3px;
    }
  }

  ${(props) =>
    `${mainCssRules({
      baseFontSize: props.$baseFontSize,
      baseFontSizeRaw: props.$baseFontSizeRaw,
      fonts: props.$fonts,
      theme: props.theme,
    })}`};
  ${(props) => `${modifierStyled("audio:", props)}`};
  /* the player is interactive: sit above the invisible tap-navigation
     overlays or play/seek clicks land on Previous/Next instead */
  position: relative;
  z-index: 101;
`;
export default StyledAudioPlayer;
